import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Icon,
  Flex,
  Badge,
  Stack,
  Card,
} from '@chakra-ui/react';
import { FiUsers, FiCalendar, FiFileText, FiTrendingUp, FiShield, FiZap } from 'react-icons/fi';

function LandingPage() {
  return (
    <Box>
      {/* Navigation */}
      <Box
        as="nav"
        bg="white"
        boxShadow="sm"
        borderBottomWidth="1px"
        borderBottomColor="gray.200"
        position="sticky"
        top="0"
        zIndex="sticky"
      >
        <Container maxW="container.xl">
          <Flex h="16" alignItems="center" justifyContent="space-between">
            <Heading
              as="h1"
              size="md"
              bgGradient="linear(to-r, brand.darkPurple, brand.purple)"
              bgClip="text"
            >
              Radiancebook EMR
            </Heading>
            <HStack spacing={4}>
              <Text fontSize="sm" color="gray.600">
                Access your clinic portal with your custom URL
              </Text>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={8} textAlign="center" maxW="3xl" mx="auto">
            <Badge colorScheme="purple" fontSize="sm" px={3} py={1} borderRadius="full">
              Modern Medical Aesthetics Platform
            </Badge>
            <Heading
              as="h1"
              size="3xl"
              bgGradient="linear(to-r, brand.darkPurple, brand.purple)"
              bgClip="text"
              lineHeight="1.2"
            >
              Beautiful EMR for Beautiful Results
            </Heading>
            <Text fontSize="xl" color="gray.600" maxW="2xl">
              Streamline your medical aesthetics practice with our intuitive electronic medical
              records system designed specifically for cosmetic clinics.
            </Text>
            <Card
              w="full"
              maxW="2xl"
              bg="white"
              p={6}
              borderRadius="xl"
              boxShadow="lg"
            >
              <VStack spacing={4}>
                <Heading size="md" color="brand.darkPurple">
                  Access Your Clinic Portal
                </Heading>
                <Text color="gray.600" textAlign="center">
                  Each clinic has its own dedicated portal. Please use your clinic's custom URL to log in:
                </Text>
                <Box
                  w="full"
                  p={4}
                  bg="purple.50"
                  borderRadius="md"
                  fontFamily="mono"
                  fontSize="sm"
                  textAlign="center"
                  color="purple.800"
                >
                  https://<Text as="span" fontWeight="bold" color="purple.600">your-clinic</Text>.radiancebook.com
                </Box>
                <Text fontSize="sm" color="gray.500" textAlign="center">
                  Don't have a clinic URL yet? Contact us to get started.
                </Text>
              </VStack>
            </Card>
          </VStack>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxW="container.xl" py={20}>
        <VStack spacing={12}>
          <VStack spacing={4} textAlign="center">
            <Heading size="xl">Everything You Need</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              Powerful features built specifically for medical aesthetics practices
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={10} w="full">
            <FeatureCard
              icon={FiUsers}
              title="Patient Management"
              description="Comprehensive patient profiles with complete visit history, demographics, and treatment records in one place."
            />
            <FeatureCard
              icon={FiCalendar}
              title="Visit Tracking"
              description="Track all patient visits with treatment details, before/after photos, and comprehensive notes."
            />
            <FeatureCard
              icon={FiFileText}
              title="Service Catalog"
              description="Manage your treatment menu with pricing, duration, and service descriptions."
            />
            <FeatureCard
              icon={FiTrendingUp}
              title="Practice Analytics"
              description="Track revenue, popular treatments, and patient retention metrics."
            />
            <FeatureCard
              icon={FiShield}
              title="HIPAA Compliant"
              description="Secure, encrypted patient data storage with role-based access control."
            />
            <FeatureCard
              icon={FiZap}
              title="Fast & Intuitive"
              description="Modern interface designed for speed and ease of use during busy clinic hours."
            />
          </SimpleGrid>
        </VStack>
      </Container>

      {/* CTA Section */}
      <Box bg="brand.darkPurple" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={8} textAlign="center" color="white">
            <Heading size="2xl">Ready to Transform Your Practice?</Heading>
            <Text fontSize="xl" maxW="2xl" opacity={0.9}>
              Join medical aesthetics practices that trust Radiancebook EMR for their patient management.
            </Text>
            <Box>
              <Text fontSize="lg" mb={2}>
                Ready to modernize your practice?
              </Text>
              <Text fontSize="sm" opacity={0.9}>
                Contact us at info@radiancebook.com to get your clinic URL
              </Text>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* Footer */}
      <Box bg="gray.900" color="white" py={10}>
        <Container maxW="container.xl">
          <Stack
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align="center"
            spacing={4}
          >
            <Text fontSize="sm">
              © 2025 Radiancebook EMR. All rights reserved.
            </Text>
            <HStack spacing={6}>
              <Text fontSize="sm" cursor="pointer" _hover={{ color: 'brand.purple' }}>
                Privacy Policy
              </Text>
              <Text fontSize="sm" cursor="pointer" _hover={{ color: 'brand.purple' }}>
                Terms of Service
              </Text>
              <Text fontSize="sm" cursor="pointer" _hover={{ color: 'brand.purple' }}>
                Contact
              </Text>
            </HStack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <VStack
      align="start"
      p={6}
      bg="white"
      borderRadius="xl"
      boxShadow="sm"
      borderWidth="1px"
      borderColor="gray.200"
      transition="all 0.3s"
      _hover={{ boxShadow: 'lg', transform: 'translateY(-4px)' }}
    >
      <Box
        p={3}
        bg="brand.lightGray"
        borderRadius="lg"
        color="brand.darkPurple"
      >
        <Icon as={icon} boxSize={6} />
      </Box>
      <Heading size="md" color="brand.darkPurple">
        {title}
      </Heading>
      <Text color="gray.600">{description}</Text>
    </VStack>
  );
}

export default LandingPage;
